import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { TourDefinition, TourStep } from '../tour/tour.model';
import { TourService } from '../tour/tour.service';
import { InstalledSampleScenario, SampleScenarioService } from './sample-scenario.service';
import { SAMPLE_SCENARIOS, SampleScenarioId, isSampleScenarioId } from './sample-scenarios';

const TOUR_PREFIX = 'sample-scenario:';

/** Matches the submission success page (`/submission/:cp/:form[/:instance]/done`). */
const DONE_URL = /^\/submission\/[^/]+\/[^/]+(\/[^/]+)?\/done(\?|$)/;
/** Matches the form page or the choose-a-copy page reached after a scan. */
const FORM_OR_UNIT_URL = /^\/submission\/[^/]+\/[^/?]+(\/|\?|$)/;
/** Matches the form page for a specific copy (after picking a unit). */
const UNIT_FORM_URL = /^\/submission\/[^/]+\/[^/]+\/(?!choose-instance|done|history)[^/?]+(\?|$)/;

/**
 * Builds and starts the guided tour for an installed sample scenario: from
 * the checkpoint list, through printing a label / writing a tag, a (real or
 * simulated) scan, filling in the form and its history, to the sample
 * reports showing the record just made.
 *
 * Copy lives in en.json under `onboarding.tour.*`: shared steps use
 * `onboarding.tour.common.<step>`, and scenario-specific wording uses
 * `onboarding.tour.<scenario>.<step>`.
 */
@Injectable({ providedIn: 'root' })
export class SampleScenarioTourService {
  private tour = inject(TourService);
  private samples = inject(SampleScenarioService);
  private router = inject(Router);

  constructor() {
    this.tour.registerFactory(TOUR_PREFIX, (tourId) => this.rebuild(tourId));
  }

  /** Starts the tour for an installed scenario. Returns false if it isn't installed. */
  async start(id: SampleScenarioId): Promise<boolean> {
    const installed = await this.samples.getInstalledScenario(id);
    const tour = installed ? this.build(installed) : null;
    if (!tour) return false;
    await this.tour.start(tour);
    return true;
  }

  /** Resumes a tour persisted before a reload, if there is one. */
  async resume(): Promise<boolean> {
    return this.tour.resume();
  }

  /** The tour for an installed scenario, or null if it's missing pieces the tour needs. */
  build(installed: InstalledSampleScenario): TourDefinition | null {
    const id = installed.id;
    const checkpointId = installed.checkpointIds[0];
    const formId = installed.formId;
    if (!checkpointId || !formId) return null;

    const def = SAMPLE_SCENARIOS[id];
    const [mainReportId, secondReportId] = installed.reportIds;
    const isPatrol = id === 'patrol';
    const hasUnits = !!def.checkpoints[0]?.instances?.length;
    const params = { checkpoint: def.checkpoints[0].name, form: def.form.name };
    const t = (step: string) => `onboarding.tour.${id}.${step}`;
    const common = (step: string) => `onboarding.tour.common.${step}`;

    const steps: TourStep[] = [
      {
        id: 'welcome',
        titleKey: common('welcome.title'),
        bodyKey: t('welcome'),
        params,
      },
      {
        id: 'checkpoints',
        route: ['/checkpoints'],
        anchor: `view-button-${formId}`,
        titleKey: common('checkpoints.title'),
        bodyKey: t('checkpoints'),
        params,
        advanceWhenUrl: /^\/checkpoints\/[^/]+\/[^/]+\/view(\?|$)/,
      },
      {
        id: 'labels',
        route: ['/checkpoints', checkpointId, formId, 'view'],
        anchor: 'print-labels-button',
        titleKey: common('labels.title'),
        bodyKey: hasUnits ? t('labels') : common('labels.body'),
        onSiteKey: common('labels.onSite'),
        params,
      },
      {
        id: 'nfc',
        route: ['/checkpoints', checkpointId, formId, 'view'],
        anchor: 'write-nfc-tags-button',
        titleKey: common('nfc.title'),
        bodyKey: common('nfc.body'),
        onSiteKey: common('nfc.onSite'),
        params,
      },
      {
        id: 'scan',
        route: ['/scan'],
        anchor: 'start-scan-button',
        titleKey: common('scan.title'),
        bodyKey: t('scan'),
        onSiteKey: common('scan.onSite'),
        params,
        waitForUser: true,
        advanceWhenUrl: isPatrol ? DONE_URL : FORM_OR_UNIT_URL,
        action: {
          labelKey: common('scan.simulate'),
          icon: 'scan-line',
          // Exactly where a scan of this checkpoint's label/tag lands.
          run: () => void this.router.navigate(['/submission', checkpointId]),
        },
      },
    ];

    if (hasUnits) {
      steps.push({
        id: 'unit',
        titleKey: common('unit.title'),
        bodyKey: t('unit'),
        params,
        spotlight: false,
        waitForUser: true,
        advanceWhenUrl: UNIT_FORM_URL,
      });
    }

    if (!isPatrol) {
      steps.push({
        id: 'fill',
        anchor: 'submit-scan-button',
        spotlight: false,
        titleKey: common('fill.title'),
        bodyKey: t('fill'),
        params,
        waitForUser: true,
        advanceWhenUrl: DONE_URL,
      });
    }

    steps.push(
      {
        id: 'saved',
        anchor: 'view-history-button',
        titleKey: common('saved.title'),
        bodyKey: t('saved'),
        params,
      },
      {
        id: 'history',
        route: ['/submission', checkpointId, formId, 'history'],
        titleKey: common('history.title'),
        bodyKey: common('history.body'),
        params,
      },
    );

    if (mainReportId) {
      steps.push({
        id: 'report',
        route: ['/reports', mainReportId, 'results'],
        titleKey: common('report.title'),
        bodyKey: t('report'),
        params,
      });
    }
    if (secondReportId) {
      steps.push({
        id: 'reports',
        route: ['/reports'],
        anchor: `report-item-${secondReportId}`,
        titleKey: common('reports.title'),
        bodyKey: t('reports'),
        params,
      });
    }

    steps.push({
      id: 'finish',
      route: ['/onboarding'],
      anchor: `scenario-card-${id}`,
      titleKey: common('finish.title'),
      bodyKey: t('finish'),
      params,
    });

    return { id: `${TOUR_PREFIX}${id}`, steps };
  }

  private async rebuild(tourId: string): Promise<TourDefinition | null> {
    const id = tourId.slice(TOUR_PREFIX.length);
    if (!isSampleScenarioId(id)) return null;
    const installed = await this.samples.getInstalledScenario(id);
    return installed ? this.build(installed) : null;
  }
}
